/**
 * Display-only clamp of a canvas window to the phone's visible viewport.
 *
 * WHY (e2e run of 2026-09-26). Canvas nodes render at their persisted
 * `state.width`/`state.height`. A window sized on a desktop (1200 px wide) and
 * reopened on a phone lands at 100% anchored top-left (the handset framing,
 * `frameViewportForRect` with `fit: false` for app screens), so its right and
 * bottom edges sit off the screen: the app's own toolbar and its close dot
 * were unreachable, and crossing below the md breakpoint never refit. The
 * handset "fill" double-tap already sizes IN PLACE with the bottom bar
 * subtracted, but only when asked.
 *
 * The clamp is a RENDER concern, never a write: the node is drawn at
 * `min(persisted, viewport - gutter)` while handset framing is on, the store
 * keeps the desktop size, and the same window opened on a desktop again is
 * as wide as it was. The canvas keeps the clamped ids out of React Flow's
 * dimension echo (the ResizeObserver measures the clamped DOM and would
 * otherwise write that size back), exactly as the native-fullscreen hold
 * does. Pure so the rule is unit-testable
 * (tests/unit/workspaces/handset-node-clamp.test.ts).
 */

export interface HandsetClampInput {
  /** The persisted window size. */
  width: number;
  height: number;
  /**
   * The visible canvas box in SCREEN px with the mobile bottom bar already
   * subtracted (what `syncViewportToStore` computes), or null before the
   * first measurement.
   */
  viewport: { width: number; height: number } | null;
  /** Handset framing is on (`fitFramingRef.current` in the canvas). */
  fit: boolean;
}

export interface HandsetClampResult {
  width: number;
  height: number;
  /** True when either axis was reduced: the id joins the echo skip set. */
  clamped: boolean;
}

/** Breathing room so the window's edge is never flush with the bezel. */
export const HANDSET_CLAMP_GUTTER = 16;

/**
 * The smallest a clamp will draw a window. A viewport narrower than this
 * (a measurement glitch, a 0-sized box mid-layout) leaves the window alone
 * rather than collapsing it.
 */
const MIN_CLAMPED = 120;

export function clampNodeSizeForHandset(input: HandsetClampInput): HandsetClampResult {
  const { width, height, viewport, fit } = input;
  if (!fit || !viewport) return { width, height, clamped: false };
  const maxW = viewport.width - HANDSET_CLAMP_GUTTER;
  const maxH = viewport.height - HANDSET_CLAMP_GUTTER;
  const w = maxW >= MIN_CLAMPED ? Math.min(width, maxW) : width;
  const h = maxH >= MIN_CLAMPED ? Math.min(height, maxH) : height;
  return { width: w, height: h, clamped: w !== width || h !== height };
}
